/**
 * `react-native-safe-area-context`, no browser.
 *
 * Os insets vêm de `env(safe-area-inset-*)` (o `index.html` usa `viewport-fit=cover`):
 * uma sonda invisível `position: fixed` com `padding: env(...)` é lida via
 * `getComputedStyle` e relida em `resize`/`orientationchange`/`visualViewport.resize`.
 * Navegador desktop = zeros; PWA standalone no iPhone = notch/home indicator.
 *
 * No CSS prefira `var(--safe-top)` etc. (tokens.css); este hook é para quando o valor
 * entra num cálculo em JS (`insets.bottom + 30`).
 */
import {
  createContext,
  useSyncExternalStore,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
} from "react";
import s from "./safe-area.module.css";
import { estiloCss } from "@/ui/rn/estilo";

export type EdgeInsets = { top: number; right: number; bottom: number; left: number };
export type Rect = { x: number; y: number; width: number; height: number };
export type Metrics = { insets: EdgeInsets; frame: Rect };
export type Edge = "top" | "right" | "bottom" | "left";
export type EdgeMode = "off" | "additive" | "maximum";
export type Edges = readonly Edge[] | Readonly<Partial<Record<Edge, EdgeMode>>>;

const ZERO: EdgeInsets = { top: 0, right: 0, bottom: 0, left: 0 };

// ─────────────────────────────────────────────────────────────────────────────
// Store dos insets (singleton, uma sonda para o app inteiro)
// ─────────────────────────────────────────────────────────────────────────────

let insetsAtuais: EdgeInsets = ZERO;
let frameAtual: Rect = { x: 0, y: 0, width: 0, height: 0 };
let sonda: HTMLDivElement | null = null;
const ouvintes = new Set<() => void>();
let instalado = false;

function lerInsets(): EdgeInsets {
  if (typeof document === "undefined") return ZERO;
  if (!sonda || !sonda.isConnected) {
    sonda = document.createElement("div");
    sonda.setAttribute("aria-hidden", "true");
    sonda.style.cssText =
      "position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;" +
      "padding-top:env(safe-area-inset-top,0px);padding-right:env(safe-area-inset-right,0px);" +
      "padding-bottom:env(safe-area-inset-bottom,0px);padding-left:env(safe-area-inset-left,0px);";
    document.body.appendChild(sonda);
  }
  const cs = getComputedStyle(sonda);
  const n = (v: string) => {
    const x = parseFloat(v);
    return Number.isFinite(x) ? x : 0;
  };
  return { top: n(cs.paddingTop), right: n(cs.paddingRight), bottom: n(cs.paddingBottom), left: n(cs.paddingLeft) };
}

function lerFrame(): Rect {
  if (typeof window === "undefined") return frameAtual;
  return { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight };
}

function atualizar() {
  const i = lerInsets();
  const f = lerFrame();
  let mudou = false;
  if (i.top !== insetsAtuais.top || i.right !== insetsAtuais.right || i.bottom !== insetsAtuais.bottom || i.left !== insetsAtuais.left) {
    insetsAtuais = i;
    mudou = true;
  }
  if (f.width !== frameAtual.width || f.height !== frameAtual.height) {
    frameAtual = f;
    mudou = true;
  }
  if (mudou) ouvintes.forEach((l) => l());
}

function instalar() {
  if (instalado || typeof window === "undefined") return;
  instalado = true;
  insetsAtuais = lerInsets();
  frameAtual = lerFrame();
  // O env() muda depois da rotação ser aplicada — relê no próximo frame também.
  const agendar = () => {
    atualizar();
    requestAnimationFrame(atualizar);
  };
  window.addEventListener("resize", agendar);
  window.addEventListener("orientationchange", agendar);
  window.visualViewport?.addEventListener("resize", agendar);
}

function inscrever(l: () => void) {
  instalar();
  ouvintes.add(l);
  return () => {
    ouvintes.delete(l);
  };
}

const lerInsetsSnapshot = () => {
  instalar();
  return insetsAtuais;
};
const lerFrameSnapshot = () => {
  instalar();
  return frameAtual;
};
const servidorInsets = () => ZERO;
const servidorFrame = () => frameAtual;

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────

/** WEB: no nativo vem do sistema antes do primeiro render; aqui é calculado sob demanda. */
export const initialWindowMetrics: Metrics | null = null;

export const SafeAreaInsetsContext = createContext<EdgeInsets | null>(null);
export const SafeAreaFrameContext = createContext<Rect | null>(null);

export function useSafeAreaInsets(): EdgeInsets {
  return useSyncExternalStore(inscrever, lerInsetsSnapshot, servidorInsets);
}

export function useSafeAreaFrame(): Rect {
  return useSyncExternalStore(inscrever, lerFrameSnapshot, servidorFrame);
}

/** Leitura imperativa (fora de componente). */
export function obterSafeAreaInsets(): EdgeInsets {
  return lerInsetsSnapshot();
}

export type SafeAreaProviderProps = {
  children?: ReactNode;
  initialMetrics?: Metrics | null;
  /** @deprecated igual ao RN */
  initialSafeAreaInsets?: EdgeInsets | null;
  style?: CSSProperties;
};

/** Passthrough: os insets vêm do CSS, não há medição por árvore. */
export function SafeAreaProvider({ children }: SafeAreaProviderProps) {
  return <>{children}</>;
}

export function SafeAreaConsumer({ children }: { children: (insets: EdgeInsets | null) => ReactNode }) {
  const insets = useSafeAreaInsets();
  return <>{children(insets)}</>;
}

export type SafeAreaViewProps = Omit<HTMLAttributes<HTMLDivElement>, "style"> & {
  /** CSS ou estilo RN. */
  style?: any;
  /** Default: todas as bordas, modo `additive`. */
  edges?: Edges;
  mode?: "padding" | "margin";
  className?: string;

  children?: ReactNode;
  ref?: Ref<HTMLDivElement>;
};

const TODAS: Edge[] = ["top", "right", "bottom", "left"];
const PROP_CSS: Record<Edge, "Top" | "Right" | "Bottom" | "Left"> = {
  top: "Top",
  right: "Right",
  bottom: "Bottom",
  left: "Left",
};

function lerComprimento(v: unknown): string | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") return `${v}px`;
  return String(v);
}

/**
 * View com padding (ou margin) igual ao inset de cada borda pedida. Como no RN, o inset
 * SOMA ao padding do `style` (`additive`) ou usa o maior dos dois (`maximum`).
 * WEB: padding vindo de `className` é sobrescrito nas bordas ativas — passe-o em `style`.
 */
export function SafeAreaView({ edges, mode = "padding", className, style, children, ref, ...rest }: SafeAreaViewProps) {
  const modos: Partial<Record<Edge, EdgeMode>> = {};
  if (!edges) {
    for (const e of TODAS) modos[e] = "additive";
  } else if (Array.isArray(edges)) {
    for (const e of edges as readonly Edge[]) modos[e] = "additive";
  } else {
    Object.assign(modos, edges);
  }

  const extra: Record<string, string> = {};
  const st = (style ?? {}) as Record<string, unknown>;
  for (const e of TODAS) {
    const m = modos[e];
    if (!m || m === "off") continue;
    const lado = PROP_CSS[e];
    const propLado = `${mode}${lado}`;
    const base =
      lerComprimento(st[propLado]) ??
      lerComprimento(st[e === "top" || e === "bottom" ? `${mode}Block` : `${mode}Inline`]) ??
      lerComprimento(st[mode]) ??
      "0px";
    const env = `env(safe-area-inset-${e}, 0px)`;
    extra[propLado] = m === "maximum" ? `max(${env}, ${base})` : `calc(${env} + ${base})`;
  }

  return (
    <div {...rest} ref={ref} className={className ? `${s.safeArea} ${className}` : s.safeArea} style={{ ...(estiloCss(style as any) ?? {}), ...extra }}>
      {children}
    </div>
  );
}

export default SafeAreaView;
